/**
 * The HTTP layer.
 *
 * Paths are always relative. In dev Vite proxies /v1 to the backend and in
 * production nginx does the same, so the browser only ever talks to its own
 * origin and no CORS preflight is ever involved. Nothing here should learn an
 * absolute API host.
 */
import { getSession } from './session';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Parsed response body, when there was one. */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Options = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** A plain object is sent as JSON; a FormData is sent as multipart (the
   *  browser sets the boundary), which is how attachment/voice uploads go. */
  body?: unknown;
  /** Skip the Authorization header. Sign-in endpoints are AllowAny. */
  anonymous?: boolean;
  /**
   * A checkout token, for the ONE case where there is no session: the
   * app opens `/wallet/checkout/<id>?t=...` in an in-app browser, which
   * carries no cookie and no bearer. It goes on the query string because
   * that is where the server reads it from, and it authorises exactly
   * one deposit - see `wallet/checkout_token.py`. Never a substitute for
   * signing in anywhere else.
   */
  checkoutToken?: string | null;
  signal?: AbortSignal;
  /** Extra request headers (the sign-in routes' app check). They are laid
   *  down FIRST, so they can add to the request but never replace the
   *  bearer or the tenant. */
  headers?: Record<string, string>;
};

/** Pull the most useful sentence out of a DRF error body. */
function messageFrom(body: unknown, status: number): string {
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (typeof b.detail === 'string') return b.detail;
    // The wallet's envelope: {"error": {"code": "below_minimum", "message": …}}.
    // Without this branch every money error reads "Request failed (400)",
    // which tells somebody nothing about a minimum they just missed.
    if (b.error && typeof b.error === 'object') {
      const inner = b.error as Record<string, unknown>;
      if (typeof inner.message === 'string' && inner.message) return inner.message;
      if (typeof inner.code === 'string') return inner.code.replace(/_/g, ' ');
    }
    // DRF field errors: {"phone": ["This field is required."]}
    for (const value of Object.values(b)) {
      if (Array.isArray(value) && typeof value[0] === 'string') return value[0];
    }
  }
  return `Request failed (${status}).`;
}

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false, signal, checkoutToken } = opts;
  const headers: Record<string, string> = {
    // What this client can draw (feed/serializers.py reads it): a voice
    // comment is played here (@nsibi/feed-web VoiceNote), not told in words.
    'X-Nsibi-Features': 'voice-comments',
    ...opts.headers,
    Accept: 'application/json',
  };

  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  // Never set Content-Type for FormData: the browser has to add the multipart
  // boundary, and a manual application/json here silently breaks the upload.
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';

  // A real session always wins. The token is the fallback for a browser
  // that has none, and it is deliberately NOT merged with the bearer:
  // the server prefers the session too, so sending both would only make
  // the weaker credential look load-bearing when it is not.
  const session = anonymous ? null : getSession();
  if (session) {
    headers.Authorization = `Bearer ${session.token}`;
    headers['X-Nsibi-Tenant'] = session.tenantSlug;
  } else if (!anonymous && !checkoutToken) {
    throw new ApiError(401, 'Not signed in.');
  }

  let url = path;
  if (!session && checkoutToken) {
    url += `${path.includes('?') ? '&' : '?'}t=${encodeURIComponent(checkoutToken)}`;
  }

  const res = await fetch(url, {
    method,
    headers,
    signal,
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
  });

  // 204 and friends have no body to parse.
  const text = await res.text();
  const parsed = text ? safeJson(text) : undefined;

  if (!res.ok) throw new ApiError(res.status, messageFrom(parsed, res.status), parsed);
  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}
